import type { FormikValues } from "formik";
import type { FC } from "react";
import Masonry from "react-responsive-masonry";
import type { MyBusinessBusinessType, MyBusinessTimeOfDay } from "shared";
import type { UseFormReturn } from "../common/form/useForm";
import { Card } from "../components/Card";
import { FormBusinessAttributes } from "./FormBusinessAttributes";
import { FormBusinessContactLocation } from "./FormBusinessContactLocation";
import { FormBusinessMedia } from "./FormBusinessMedia";
import { FormBusinessRegularHours } from "./FormBusinessRegularHours";
import { FormBusinessServiceArea } from "./FormBusinessServiceArea";
import FormBusinessServicesProducts from "./FormBusinessServicesProducts";
import { FormBusinessSpecialHours } from "./FormBusinessSpecialHours";

export interface FormMyBusinessLocationFields extends FormikValues {
	// basic information
	title?: string;
	legalName?: string;
	description?: string;
	/** National number only (digits / spacing). Country dial is separate. */
	phone?: string;
	/** E.164 dial prefix, e.g. "+91". */
	phoneCountryCode?: string;
	websiteUri?: string;
	street?: string;
	city?: string;
	regionCode?: string;
	administrativeArea?: string;
	zip?: string;
	country?: string;
	//service area
	serviceAreas?: {
		place: string;
		placeId: string;
	}[];
	businessType?: MyBusinessBusinessType;
	// business hours
	monday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	tuesday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	wednesday?: [
		MyBusinessTimeOfDay | undefined,
		MyBusinessTimeOfDay | undefined,
	];
	thursday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	friday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	saturday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	sunday?: [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined];
	/** Holiday / special hours periods (master + GBP specialHours). */
	specialHours?: Array<{
		id?: string;
		startDate?: string; // YYYY-MM-DD
		endDate?: string;
		closed?: boolean;
		openTime?: MyBusinessTimeOfDay;
		closeTime?: MyBusinessTimeOfDay;
	}>;
	//services and products
	category?: string;
	additionalCategories?: string[];
	structuredServices?: string[];
	freeformServices?: { name: string; category: string }[];
	// business attributes
	boolAttributes?: { id: string; value: boolean }[];
	urlAttributes?: { id: string; url: string }[];
}

export type Props = {
	form: UseFormReturn<FormMyBusinessLocationFields>;
	locationID: string;
};

export const FormBusinessLocationFields: FC<Readonly<Props>> = ({
	form,
	locationID,
}) => {
	return (
		<Masonry gutter={"1rem"} columnsCount={2}>
			<Card>
				<Card.Header>Contact & Location</Card.Header>
				<Card.Body>
					<FormBusinessContactLocation form={form} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Service Area</Card.Header>
				<Card.Body>
					<FormBusinessServiceArea form={form} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Business Hours</Card.Header>
				<Card.Body>
					<FormBusinessRegularHours form={form} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Holiday Hours</Card.Header>
				<Card.Body>
					<FormBusinessSpecialHours form={form} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Services & Products</Card.Header>
				<Card.Body>
					<FormBusinessServicesProducts form={form} locationID={locationID} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Business Attributes</Card.Header>
				<Card.Body>
					<FormBusinessAttributes form={form} locationID={locationID} />
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Photos & Media</Card.Header>
				<Card.Body>
					<FormBusinessMedia locationID={locationID} />
				</Card.Body>
			</Card>
		</Masonry>
	);
};
